import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Menu, X, ChevronDown, 
  Utensils, BedDouble, Armchair, Bath, 
  Shirt, Maximize, Monitor, Home,
  Hammer, Palette, Ruler,
  FileText, Users, HelpCircle
} from 'lucide-react';

import { useConsultation } from '../../context/ConsultationContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  const [galleryOpen, setGalleryOpen] = useState(false); 
  const [servicesOpen, setServicesOpen] = useState(false);
  const [discoverOpen, setDiscoverOpen] = useState(false);

  const { openModal } = useConsultation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // SEO Optimized Menu Items
  const galleryItems = [
    { name: 'Modular Kitchen Design', icon: <Utensils size={16} />, link: '/gallery/kitchen' },
    { name: 'Luxury Bedroom Interior', icon: <BedDouble size={16} />, link: '/gallery/bedroom' },
    { name: 'Living Room Decor', icon: <Armchair size={16} />, link: '/gallery/living-room' },
    { name: 'Modern Bathroom Design', icon: <Bath size={16} />, link: '/gallery/bathroom' },
    { name: 'Walk-in Wardrobes', icon: <Shirt size={16} />, link: '/gallery/wardrobe' },
    { name: 'Space Saving Furniture', icon: <Maximize size={16} />, link: '/gallery/space-saving' },
    { name: 'Home Office Interiors', icon: <Monitor size={16} />, link: '/gallery/luxury-office' },
    { name: 'Full Home Interiors', icon: <Home size={16} />, link: '/gallery/interiors' },
  ];

  const serviceItems = [
    { name: 'Home Construction & Builders', icon: <Hammer size={16} />, link: '/services/builders' },
    { name: 'Turnkey Interior Design', icon: <Palette size={16} />, link: '/services/interior-design' },
    { name: 'Architectural Planning 2D/3D', icon: <Ruler size={16} />, link: '/services/architecture' },
  ];

  const discoverItems = [
    { name: 'Client Testimonials', icon: <Users size={16} />, link: '/stories' },
    { name: 'Design Trends Blog', icon: <FileText size={16} />, link: '/blogs' },
    { name: 'FAQs & Pricing', icon: <HelpCircle size={16} />, link: '/faqs' },
  ];

  const linkStyle = `text-sm tracking-[1.5px] uppercase font-semibold transition-colors duration-300 cursor-pointer flex items-center gap-1 ${
    scrolled ? 'text-gray-800 hover:text-amber-600' : 'text-white hover:text-amber-400'
  }`;

  return (
    <>
      <nav 
        role="navigation" 
        aria-label="Main Navigation"
        className={`fixed w-full z-50 transition-all duration-500 ease-in-out ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-md py-2 shadow-md' 
          : 'bg-gradient-to-b from-black/70 to-transparent py-8'
      }`}>
        <div className="max-w-7xl mx-auto px-6">
          
          {/* DESKTOP LAYOUT */}
          <div className="hidden md:flex justify-between items-center w-full">
            
            <div className="flex space-x-8 lg:space-x-12 flex-1 justify-end pr-8 lg:pr-12 relative">
              <Link to="/about" className={linkStyle} title="About Luxuriya Homes">About</Link>
              
              {/* GALLERY DROPDOWN */}
              <div className="group relative h-full flex items-center" aria-haspopup="true">
                <span className={linkStyle}>
                  Designs <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-300"/>
                </span>
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-6 w-[550px] bg-white text-gray-800 shadow-2xl rounded-sm border-t-4 border-amber-500 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform group-hover:translate-y-0 translate-y-4">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-t border-l border-gray-100 rotate-45 transform"></div>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-4 p-8">
                    {galleryItems.map((item, idx) => (
                      <Link 
                        key={idx} 
                        to={item.link}
                        className="flex items-center gap-3 text-sm font-medium hover:text-amber-600 transition-colors group/item p-2 hover:bg-gray-50 rounded-md text-left w-full"
                        title={`View ${item.name}`}
                      >
                        <span className="text-gray-400 group-hover/item:text-amber-500 transition-colors">
                          {item.icon}
                        </span>
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* SERVICES DROPDOWN */}
              <div className="group relative h-full flex items-center" aria-haspopup="true">
                <span className={linkStyle}>
                  Services <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-300"/>
                </span>
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-6 w-80 bg-white text-gray-800 shadow-2xl rounded-sm border-t-4 border-amber-500 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform group-hover:translate-y-0 translate-y-4">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-t border-l border-gray-100 rotate-45 transform"></div>
                  <div className="flex flex-col gap-2 p-6">
                    {serviceItems.map((item, idx) => (
                      <Link 
                        key={idx} 
                        to={item.link}
                        className="flex items-center gap-3 text-sm font-medium hover:text-amber-600 transition-colors group/item p-3 hover:bg-gray-50 rounded-md text-left w-full"
                        title={`Our Services: ${item.name}`}
                      >
                        <span className="text-gray-400 group-hover/item:text-amber-500 transition-colors">
                          {item.icon}
                        </span>
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* CENTER LOGO */}
            <Link to="/" aria-label="Luxuriya Homes" className="group flex-shrink-0 relative z-10 px-6">
              <div className={`flex flex-col items-center transition-all duration-500 ease-in-out origin-center ${
                  scrolled ? 'scale-75' : 'scale-100'
              }`}>
                  <span className={`text-3xl mb-1 ${scrolled ? 'text-amber-600' : 'text-amber-400'}`}>♛</span>
                  <h1 className={`text-3xl font-serif font-bold tracking-[0.2em] uppercase ${scrolled ? 'text-black' : 'text-white'}`}>
                    Luxuriya
                  </h1>
                  <span className={`text-[0.6rem] tracking-[0.4em] uppercase mt-1 ${scrolled ? 'text-red-800' : 'text-gray-300'}`}>
                    Homes
                  </span>
              </div>
            </Link>

            {/* Right Menu */}
            <div className="flex space-x-8 lg:space-x-12 flex-1 justify-start pl-8 lg:pl-12 items-center">
              
              <div className="group relative h-full flex items-center" aria-haspopup="true">
                <span className={linkStyle}>
                  Resources <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-300"/>
                </span>
                
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-6 w-72 bg-white text-gray-800 shadow-2xl rounded-sm border-t-4 border-amber-500 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform group-hover:translate-y-0 translate-y-4">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-t border-l border-gray-100 rotate-45 transform"></div>
                  <div className="flex flex-col gap-2 p-6">
                    {discoverItems.map((item, idx) => (
                      <Link 
                        key={idx} 
                        to={item.link}
                        className="flex items-center gap-3 text-sm font-medium hover:text-amber-600 transition-colors group/item p-3 hover:bg-gray-50 rounded-md text-left w-full"
                      >
                        <span className="text-gray-400 group-hover/item:text-amber-500 transition-colors">
                          {item.icon}
                        </span>
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <Link to="/contact" className={linkStyle}>Contact</Link>
              
              <button
                onClick={() => openModal()} 
                className={`ml-2 px-6 py-2.5 border text-xs uppercase tracking-widest font-bold whitespace-nowrap transition-all duration-300 ${
                  scrolled
                    ? 'border-amber-700 text-amber-700 hover:bg-amber-700 hover:text-white'
                    : 'border-white/50 text-white bg-white/10 hover:bg-white hover:text-black'
                }`}
              >
                Book Consult
              </button>
            </div>
          </div>

          {/* MOBILE LAYOUT */}
          <div className="md:hidden flex justify-between items-center transition-all duration-500">
            <Link to="/" className={`font-serif font-bold tracking-widest transition-all ${
              scrolled ? 'text-lg text-amber-600' : 'text-xl text-amber-500'
            }`}>
              LUXURIYA
            </Link>
            <button 
                onClick={() => setIsOpen(!isOpen)} 
                className={scrolled ? 'text-black' : 'text-white'}
            >
              {isOpen ? <X size={24} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* MOBILE MENU OVERLAY */}
        {isOpen && (
          <div className="absolute top-full left-0 w-full h-[calc(100vh-80px)] bg-white flex flex-col pt-10 px-8 space-y-6 md:hidden overflow-y-auto shadow-xl border-t border-gray-100">
            <Link to="/about" onClick={() => setIsOpen(false)} className="text-xl font-serif text-gray-800">About Us</Link>
            
            {/* MOBILE DESIGNS */}
            <div>
              <button 
                onClick={() => setGalleryOpen(!galleryOpen)} 
                className="flex items-center justify-between w-full text-xl font-serif text-gray-800 mb-4"
              >
                Designs & Styles <ChevronDown size={20} className={`transform transition-transform ${galleryOpen ? 'rotate-180' : ''}`}/>
              </button>
              {galleryOpen && (
                <div className="grid grid-cols-1 gap-3 pl-4 mb-4 border-l-2 border-amber-100">
                  {galleryItems.map((item, idx) => (
                    <Link 
                      key={idx} 
                      to={item.link}
                      onClick={() => setIsOpen(false)} 
                      className="flex items-center gap-3 text-sm text-gray-600 py-1 w-full text-left"
                    >
                      {item.icon} {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* MOBILE SERVICES */}
            <div>
              <button 
                onClick={() => setServicesOpen(!servicesOpen)} 
                className="flex items-center justify-between w-full text-xl font-serif text-gray-800 mb-4"
              >
                Our Services <ChevronDown size={20} className={`transform transition-transform ${servicesOpen ? 'rotate-180' : ''}`}/>
              </button>
              {servicesOpen && (
                <div className="grid grid-cols-1 gap-3 pl-4 mb-4 border-l-2 border-amber-100">
                  {serviceItems.map((item, idx) => (
                    <Link 
                      key={idx} 
                      to={item.link}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 text-sm text-gray-600 py-1 w-full text-left"
                    >
                      {item.icon} {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* MOBILE RESOURCES - ADDED THIS SECTION */}
            <div>
              <button 
                onClick={() => setDiscoverOpen(!discoverOpen)} 
                className="flex items-center justify-between w-full text-xl font-serif text-gray-800 mb-4"
              >
                Resources <ChevronDown size={20} className={`transform transition-transform ${discoverOpen ? 'rotate-180' : ''}`}/>
              </button>
              {discoverOpen && (
                <div className="grid grid-cols-1 gap-3 pl-4 mb-4 border-l-2 border-amber-100">
                  {discoverItems.map((item, idx) => (
                    <Link 
                      key={idx} 
                      to={item.link}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 text-sm text-gray-600 py-1 w-full text-left"
                    >
                      {item.icon} {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/contact" onClick={() => setIsOpen(false)} className="text-xl font-serif text-gray-800">Contact</Link>
            
            <button
              onClick={() => {
                setIsOpen(false);   
                openModal();
              }}
              className="w-full text-center py-4 bg-black text-white uppercase tracking-widest text-sm mt-4 font-bold shadow-lg"
            >
              Get Free Consultation
            </button>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;